/**
 * @tanstack/react-query hooks for the Shift + Roster + Swap module
 * (migration 057). Thin wrappers over lib/api/shifts.ts.
 *
 * Query-key convention:
 *   ["shifts", <area>, ...params]  — flush whole module with ["shifts"]
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { shiftsApi } from "@/lib/api";
import { invalidateApprovalsSummary } from "@/lib/approvals-invalidate";
import type {
  CreateShiftRequest,
  CreateSwapRequest,
  DecisionRequest,
  RosterUpsertItem,
  UpdateShiftRequest,
} from "@/types/attendance";

export const shiftKeys = {
  all: ["shifts"] as const,
  defs: (includeInactive?: boolean) =>
    [...shiftKeys.all, "defs", includeInactive ?? false] as const,
  roster: (params?: Record<string, unknown>) =>
    [...shiftKeys.all, "roster", JSON.stringify(params ?? {})] as const,
  swaps: (status?: string, scope?: string, excludeSelf?: boolean) =>
    [
      ...shiftKeys.all,
      "swaps",
      status ?? "all",
      excludeSelf ? "excl-self" : (scope ?? "role"),
    ] as const,
};

const CATALOGUE_STALE = 5 * 60 * 1000;

// ---------------------------------------------------------------------------
// Shift definitions
// ---------------------------------------------------------------------------

export function useShifts(includeInactive = false) {
  return useQuery({
    queryKey: shiftKeys.defs(includeInactive),
    queryFn: ({ signal }) =>
      shiftsApi.listShifts(
        includeInactive ? { include_inactive: true } : undefined,
        signal,
      ),
    staleTime: CATALOGUE_STALE,
  });
}

export function useCreateShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateShiftRequest) => shiftsApi.createShift(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: shiftKeys.all }),
  });
}

export function useUpdateShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: UpdateShiftRequest }) =>
      shiftsApi.updateShift(vars.id, vars.body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: shiftKeys.all }),
  });
}

export function useDeleteShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => shiftsApi.deleteShift(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: shiftKeys.all }),
  });
}

// ---------------------------------------------------------------------------
// Roster
// ---------------------------------------------------------------------------

export function useRoster(params?: {
  from?: string;
  to?: string;
  date?: string;
  employee_id?: number;
}) {
  return useQuery({
    queryKey: shiftKeys.roster(params),
    queryFn: ({ signal }) => shiftsApi.listRoster(params, signal),
  });
}

export function useUpsertRoster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assignments: RosterUpsertItem[]) =>
      shiftsApi.upsertRoster(assignments),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: shiftKeys.all }),
  });
}

export function useDeleteRoster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => shiftsApi.deleteRoster(id),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: shiftKeys.all }),
  });
}

// ---------------------------------------------------------------------------
// Swaps
// ---------------------------------------------------------------------------

export function useSwaps(
  status?: string,
  scope?: "self",
  opts?: { excludeSelf?: boolean },
) {
  return useQuery({
    queryKey: shiftKeys.swaps(status, scope, opts?.excludeSelf),
    queryFn: ({ signal }) =>
      shiftsApi.listSwaps(
        {
          ...(status ? { status } : {}),
          ...(scope ? { scope } : {}),
          ...(opts?.excludeSelf ? { exclude_self: true } : {}),
        },
        signal,
      ),
  });
}

function invalidateSwaps(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: shiftKeys.all });
  void qc.invalidateQueries({ queryKey: ["notifications"] });
  // The Inbox / tab badges count swaps awaiting the caller.
  invalidateApprovalsSummary(qc);
}

export function useCreateSwap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSwapRequest) => shiftsApi.createSwap(body),
    onSuccess: () => invalidateSwaps(qc),
  });
}

export function useAcceptSwap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => shiftsApi.acceptSwap(id),
    onSuccess: () => invalidateSwaps(qc),
  });
}

export function useApproveSwap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body?: DecisionRequest }) =>
      shiftsApi.approveSwap(vars.id, vars.body),
    onSuccess: () => invalidateSwaps(qc),
  });
}

export function useRejectSwap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body?: DecisionRequest }) =>
      shiftsApi.rejectSwap(vars.id, vars.body),
    onSuccess: () => invalidateSwaps(qc),
  });
}

export function useCancelSwap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => shiftsApi.cancelSwap(id),
    onSuccess: () => invalidateSwaps(qc),
  });
}
